import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import showToast from '../../utils/toastHelper';
import paymentService from '../../services/api/paymentService';

const VotingPage = () => {
  const { electionId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [election, setElection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [votingCandidateId, setVotingCandidateId] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState(null);
  const [pendingVote, setPendingVote] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  // Kiosk mode state
  const [isKioskMode, setIsKioskMode] = useState(false);
  const [showKioskDone, setShowKioskDone] = useState(false);
  const [showEndSessionPin, setShowEndSessionPin] = useState(false);
  const [kioskPin, setKioskPin] = useState('');
  const [endingSession, setEndingSession] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [kioskCountdown, setKioskCountdown] = useState(10);

  useEffect(() => {
    // Check for session ID from magic link
    const sessionIdParam = searchParams.get('sessionId');
    if (sessionIdParam) {
      setSessionId(sessionIdParam);
    }

    // Check for kiosk mode
    const votingMode = sessionStorage.getItem('kioskVotingMode');
    if (votingMode === 'id_based') {
      setIsKioskMode(true);
    }
    
    fetchElection();

    // Refetch when the voter returns to this tab (e.g. after checking another page).
    // Post-vote refresh already happens in submitVote(), so no interval needed.
    const handleFocus = () => fetchElection();
    window.addEventListener('focus', handleFocus);

    return () => window.removeEventListener('focus', handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [electionId]);

  const fetchElection = async () => {
    try {
      const currentSessionId = sessionId || searchParams.get('sessionId');
      const headers = {};
      
      if (currentSessionId) {
        // Use session-based auth for magic link voting
        headers['X-Session-ID'] = currentSessionId;
      } else {
        // Use traditional JWT auth
        const token = localStorage.getItem('token');
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/elections/${electionId}/vote`, {
        headers,
      });
      const freshData = response.data.data;
      // Force new object reference to trigger re-render
      setElection({...freshData});

      // Kiosk mode: check completion from fresh data (not stale state).
      // This catches both the post-vote case AND the initial load where
      // a voter has already voted on all positions.
      const votingMode = sessionStorage.getItem('kioskVotingMode');
      if (votingMode === 'id_based' && freshData.positions) {
        const votable = freshData.positions.filter(p => p.candidates && p.candidates.length > 0);
        if (votable.length > 0 && votable.every(p => p.userHasVoted)) {
          setShowKioskDone(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch election:', error);
      // Don't show toast on polling errors to avoid spam
      // Only show error on initial load
      if (loading) {
        showToast(error.response?.data?.error || 'Failed to load election', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (positionId, candidateId, requiresPayment = false) => {
    const position = election.positions.find(p => p.id === positionId);
    
    // Check if trying to vote for different candidate
    if (position.userHasVoted && position.votedCandidateId !== candidateId) {
      showToast('You can only vote multiple times for the same candidate', 'warning');
      return;
    }

    // Check if already voted and not multi-vote enabled
    if (position.userHasVoted && !position.multiVoteEnabled) {
      showToast('You have already voted for this category', 'info');
      return;
    }

    // Check max votes limit
    if (position.multiVoteEnabled && position.maxVotesPerUser && position.userVoteCount >= position.maxVotesPerUser) {
      showToast(`Maximum ${position.maxVotesPerUser} votes allowed for this category`, 'warning');
      return;
    }

    // Determine if payment is required (additional vote on same candidate)
    const needsPayment = position.userHasVoted && 
                        position.votedCandidateId === candidateId && 
                        position.multiVoteEnabled && 
                        position.votePrice > 0;

    // If requires payment, initiate payment flow
    if (needsPayment) {
      initiatePayment(positionId, candidateId);
    } else {
      submitVote(positionId, candidateId);
    }
  };

  const initiatePayment = async (positionId, candidateId) => {
    setVotingCandidateId(candidateId);
    try {
      // Check payment options first
      const response = await paymentService.checkPaymentOptions(positionId, candidateId);
      
      if (response.success && response.data.requiresPayment) {
        // Store pending vote info
        setPendingVote({ positionId, candidateId });
        setPaymentOptions(response.data);
        setShowPaymentModal(true);
        setVotingCandidateId(null);
      } else if (!response.data.canVote) {
        showToast(response.data.error || 'Cannot vote', 'error');
        setVotingCandidateId(null);
      }
    } catch (error) {
      console.error('Failed to check payment options:', error);
      showToast(error.response?.data?.error || 'Failed to check payment options', 'error');
      setVotingCandidateId(null);
    }
  };

  const handlePaymentMethodSelection = async (paymentMethod) => {
    if (!pendingVote) return;
    
    setShowPaymentModal(false);
    setVotingCandidateId(pendingVote.candidateId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/payments/initiate`,
        { 
          positionId: pendingVote.positionId, 
          candidateId: pendingVote.candidateId,
          paymentMethod 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        if (paymentMethod === 'wallet') {
          // Wallet payment successful, vote was cast
          showToast('Vote cast successfully using wallet!', 'success');
          fetchElection(); // Refresh to update vote status
        } else {
          // Direct payment - redirect to Flutterwave
          localStorage.setItem('pendingVoteElectionId', electionId);
          localStorage.setItem('sidebarVisible', 'false');
          window.location.href = response.data.data.paymentLink;
        }
      }
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      showToast(error.response?.data?.error || 'Failed to initiate payment', 'error');
    } finally {
      setVotingCandidateId(null);
      setPendingVote(null);
    }
  };

  const submitVote = async (positionId, candidateId, transactionId = null) => {
    setVotingCandidateId(candidateId);
    try {
      const currentSessionId = sessionId || searchParams.get('sessionId');
      const headers = {};
      
      if (currentSessionId) {
        headers['X-Session-ID'] = currentSessionId;
      } else {
        const token = localStorage.getItem('token');
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      await axios.post(
        `${process.env.REACT_APP_API_URL}/votes`,
        { positionId, candidateId, transactionId, sessionId: currentSessionId },
        { headers }
      );
      showToast('Vote cast successfully!', 'success');
      
      // NOTE: We don't mark session as voted here because sessions can be used
      // to vote across multiple positions. The voting service handles per-position
      // voting rules (already voted, multi-vote, etc.)
      
      await fetchElection(); // Refresh to update vote status — also triggers kiosk completion check
    } catch (error) {
      console.error('Failed to cast vote:', error);
      showToast(error.response?.data?.error || 'Failed to cast vote', 'error');
    } finally {
      setVotingCandidateId(null);
    }
  };

  // Auto-advance to next voter after countdown when voting is complete
  useEffect(() => {
    if (!showKioskDone) return;
    let count = 10;
    setKioskCountdown(10);
    const timer = setInterval(() => {
      count -= 1;
      setKioskCountdown(count);
      if (count <= 0) {
        clearInterval(timer);
        handleNextVoter();
      }
    }, 1000);
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showKioskDone]);

  const handleNextVoter = () => {
    // Clear session data and go back to ID entry
    sessionStorage.removeItem('kioskVoterName');
    sessionStorage.removeItem('kioskVotingMode');
    const kioskElectionId = sessionStorage.getItem('kioskElectionId');
    sessionStorage.removeItem('kioskElectionId');
    navigate(`/vote/id/${kioskElectionId || electionId}`);
  };

  const handleEndSession = async () => {
    if (!kioskPin || kioskPin.length !== 4) {
      showToast('Please enter a 4-digit PIN', 'error');
      return;
    }
    setEndingSession(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/voting-auth/end-kiosk-session`, {
        electionId,
        pin: kioskPin,
      });
      if (response.data.success) {
        sessionStorage.clear();
        setSessionEnded(true);
        setShowEndSessionPin(false);
        setShowKioskDone(false);
      }
    } catch (err) {
      showToast(err.response?.data?.error || 'Invalid PIN', 'error');
    } finally {
      setEndingSession(false);
    }
  };

  // Session ended screen
  if (sessionEnded) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="text-center text-white">
          <i className="bi bi-lock-fill" style={{ fontSize: '4rem' }}></i>
          <h3 className="mt-3">Voting Session Ended</h3>
          <p className="opacity-75">The voting session has been closed by the administrator.</p>
        </div>
      </div>
    );
  }

  // Kiosk "You're done" screen
  if (showKioskDone) {
    const voterName = sessionStorage.getItem('kioskVoterName') || 'Voter';
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center" style={{ background: '#f8f9fa' }}>
        <div style={{ maxWidth: '500px', width: '100%', padding: '0 1rem' }}>
          <div className="card shadow-lg text-center" style={{ borderRadius: '16px', border: 'none' }}>
            <div className="card-body p-5">
              <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem', color: 'white', fontSize: '2.5rem' }}>
                <i className="bi bi-check-lg"></i>
              </div>
              <h3 style={{ fontWeight: '700', color: '#2c3e50' }}>Thank You!</h3>
              <p className="text-muted mb-2">Thank you, <strong>{voterName}</strong>. Your votes have been recorded.</p>
              <p className="mb-4" style={{ fontSize: '0.95rem', color: '#667eea', fontWeight: '600' }}>
                <i className="bi bi-arrow-clockwise me-1"></i>
                Next voter in {kioskCountdown}s…
              </p>
              <button onClick={handleNextVoter} className="btn btn-lg w-100 mb-3"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '12px', fontWeight: '600' }}>
                <i className="bi bi-person-plus me-2"></i>Next Voter Now
              </button>
              <button onClick={() => setShowEndSessionPin(true)} className="btn btn-sm btn-outline-secondary">
                <i className="bi bi-lock me-1"></i>End Voting Session
              </button>
            </div>
          </div>

          {/* PIN prompt */}
          {showEndSessionPin && (
            <div className="card mt-3" style={{ borderRadius: '12px' }}>
              <div className="card-body p-3">
                <label className="form-label" style={{ fontWeight: '600', fontSize: '0.875rem' }}>Enter Admin PIN to end session</label>
                <div className="d-flex gap-2">
                  <input type="password" maxLength="4" className="form-control text-center"
                    value={kioskPin} onChange={(e) => setKioskPin(e.target.value.replace(/\D/g, ''))} placeholder="••••"
                    style={{ fontSize: '1.25rem', letterSpacing: '8px', maxWidth: '150px' }} />
                  <button onClick={handleEndSession} className="btn btn-danger" disabled={endingSession || kioskPin.length !== 4}>
                    {endingSession ? '...' : 'End'}
                  </button>
                  <button onClick={() => { setShowEndSessionPin(false); setKioskPin(''); }} className="btn btn-secondary">Cancel</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!election) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">Nomination not found or not available for voting</p>
        <Link to="/voter/organizations" className="btn btn-primary mt-3">
          Back to Organizations
        </Link>
      </div>
    );
  }

  const kioskVoterName = sessionStorage.getItem('kioskVoterName');

  // Hide live candidate vote tallies for private (closed) and school/kiosk (id_based)
  // elections so counts don't influence voters. Public standard elections still show them.
  const showVoteCounts = election.accessType === 'open' && election.votingMode !== 'id_based';

  return (
    <div className="container-fluid">
      {/* Kiosk mode banner */}
      {isKioskMode && kioskVoterName && (
        <div className="mb-3 d-flex justify-content-between align-items-center px-3 py-2" style={{
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '10px',
        }}>
          <div className="d-flex align-items-center">
            <div style={{
              width: '36px', height: '36px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: '700', fontSize: '1rem', marginRight: '0.75rem',
            }}>
              {kioskVoterName.charAt(0)}
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#166534', fontSize: '0.9rem' }}>Voting as: {kioskVoterName}</div>
              <div style={{ fontSize: '0.75rem', color: '#4ade80' }}>Kiosk Mode</div>
            </div>
          </div>
          <div className="d-flex gap-2">
            <button onClick={handleNextVoter} className="btn btn-sm btn-outline-success" style={{ fontSize: '0.8rem' }}>
              <i className="bi bi-person-plus me-1"></i>Switch Voter
            </button>
          </div>
        </div>
      )}

      {/* Only show back button for authenticated users, not magic link sessions */}
      {!sessionId && !isKioskMode && (
        <div className="mb-4">
          <button onClick={() => navigate(-1)} style={{ color: '#6b7280', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer' }}>
            <i className="bi bi-arrow-left me-2"></i>Back
          </button>
        </div>
      )}

      {/* Nomination Header */}
      <div className="card mb-4" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
        <div className="card-body">
          <div className="d-flex align-items-center">
            {election.organization?.logoUrl && (
              <img src={election.organization.logoUrl} alt="" style={{ height: '64px', width: '64px', objectFit: 'contain', marginRight: '1.25rem', borderRadius: '12px', background: 'white', padding: '6px' }} />
            )}
            <div>
              <h2 style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{election.name}</h2>
              {election.organization?.name && (
                <small style={{ opacity: 0.8 }}>{election.organization.name}</small>
              )}
              {election.description && (
                <p style={{ opacity: 0.9, marginBottom: 0, marginTop: '0.25rem', fontSize: '0.9rem' }}>{election.description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Categories and Voting */}
      {election.positions && election.positions.filter(p => p.candidates && p.candidates.length > 0).length > 0 ? (
        election.positions.filter(p => p.candidates && p.candidates.length > 0).map((position) => (
          <div key={position.id} className="card mb-4">
            <div className="card-header bg-white" style={{ borderBottom: '2px solid #667eea' }}>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1" style={{ fontWeight: '600' }}>{position.name}</h5>
                  {position.description && (
                    <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>{position.description}</p>
                  )}
                </div>
                <div className="text-end">
                  {position.userHasVoted && (
                    <span className="badge bg-success" style={{ fontSize: '0.813rem' }}>
                      <i className="bi bi-check-circle me-1"></i>
                      Voted ({position.userVoteCount})
                    </span>
                  )}
                  {position.multiVoteEnabled && position.votePrice > 0 && (
                    <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                      Additional votes: {position.currency} {position.votePrice}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="card-body">
              {position.candidates && position.candidates.length > 0 ? (
                <div className="row justify-content-center">
                  {position.candidates.map((candidate) => (
                    <div key={candidate.id} className={`mb-4 ${
                      position.candidates.length === 1 ? 'col-md-6 col-lg-4' :
                      position.candidates.length === 2 ? 'col-md-6 col-lg-5' :
                      position.candidates.length === 3 ? 'col-md-6 col-lg-4' :
                      'col-md-6 col-lg-3'
                    }`}>
                      <div className="h-100"
                           onClick={() => {
                             setSelectedCandidate(candidate);
                             setShowCandidateModal(true);
                           }}
                           style={{
                             cursor: 'pointer',
                             transition: 'all 0.3s ease',
                             border: position.votedCandidateId === candidate.id ? '2px solid #10b981' : '1px solid #e5e7eb',
                             borderRadius: '12px',
                             overflow: 'hidden',
                             background: '#ffffff'
                           }}
                           onMouseEnter={(e) => {
                             e.currentTarget.style.transform = 'translateY(-4px)';
                             e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
                             if (position.votedCandidateId !== candidate.id) e.currentTarget.style.borderColor = '#667eea';
                           }}
                           onMouseLeave={(e) => {
                             e.currentTarget.style.transform = 'translateY(0)';
                             e.currentTarget.style.boxShadow = 'none';
                             e.currentTarget.style.borderColor = position.votedCandidateId === candidate.id ? '#10b981' : '#e5e7eb';
                           }}>
                        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
                          {/* Circular avatar */}
                          {candidate.photoUrl ? (
                            <img
                              src={candidate.photoUrl}
                              alt={candidate.user?.fullName}
                              style={{
                                width: '88px',
                                height: '88px',
                                objectFit: 'cover',
                                borderRadius: '50%',
                                border: '3px solid #f1f3f5',
                                marginBottom: '1rem'
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '88px',
                                height: '88px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '2rem',
                                fontWeight: '700',
                                margin: '0 auto 1rem',
                              }}
                            >
                              {candidate.user?.fullName?.charAt(0) || '?'}
                            </div>
                          )}
                          <h6 className="mb-1" style={{ fontWeight: '700', fontSize: '1.05rem', color: '#1e293b' }}>
                            {candidate.user?.fullName || 'Unknown'}
                          </h6>
                          {candidate.bio && (
                            <p className="mb-0" style={{ fontSize: '0.813rem', lineHeight: '1.5', color: '#64748b' }}>
                              {candidate.bio.length > 80 ? `${candidate.bio.substring(0, 80)}…` : candidate.bio}
                            </p>
                          )}
                          {showVoteCounts && candidate.voteCount !== null && candidate.voteCount !== undefined && (
                            <div className="d-flex align-items-center justify-content-center gap-1 mt-2 mb-3" style={{ fontSize: '0.813rem', color: '#94a3b8' }}>
                              <i className="bi bi-bar-chart-fill" style={{ fontSize: '0.75rem' }}></i>
                              <span>{candidate.voteCount} votes</span>
                            </div>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVote(position.id, candidate.id, position.userHasVoted && position.votePrice > 0);
                            }}
                            disabled={
                              votingCandidateId !== null || 
                              (position.userHasVoted && position.votedCandidateId !== candidate.id) ||
                              (!position.multiVoteEnabled && position.votedCandidateId === candidate.id) ||
                              (position.multiVoteEnabled && position.maxVotesPerUser && position.userVoteCount >= position.maxVotesPerUser)
                            }
                            className="btn w-100"
                            style={{
                              background: position.votedCandidateId === candidate.id 
                                ? '#10b981' 
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              border: 'none',
                              padding: '0.5rem',
                              fontSize: '0.875rem',
                              fontWeight: '600',
                              borderRadius: '8px',
                            }}
                          >
                            {votingCandidateId === candidate.id ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                Voting…
                              </>
                            ) : position.votedCandidateId === candidate.id ? (
                              <>
                                <i className="bi bi-check-circle me-1"></i>
                                Voted
                                {position.multiVoteEnabled && position.votePrice > 0 && ` (${position.currency} ${position.votePrice} to vote again)`}
                              </>
                            ) : (
                              <>
                                <i className="bi bi-hand-thumbs-up me-1"></i>
                                Vote
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-person-x" style={{ fontSize: '2rem' }}></i>
                  <p className="mt-2 mb-0" style={{ fontSize: '0.875rem' }}>No candidates for this position</p>
                </div>
              )}
            </div>
          </div>
        ))
      ) : (
        <div className="card">
          <div className="card-body text-center py-5">
            <i className="bi bi-trophy text-muted" style={{ fontSize: '3rem' }}></i>
            <p className="mt-3 text-muted">No positions available in this nomination</p>
          </div>
        </div>
      )}

      {/* Candidate Details Modal */}
      {selectedCandidate && (
        <div
          className={`modal fade ${showCandidateModal ? 'show' : ''}`}
          style={{ display: showCandidateModal ? 'block' : 'none' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ borderRadius: '12px', border: 'none', overflow: 'hidden' }}>
              {/* Avatar header */}
              <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '2rem 2rem 3.5rem', position: 'relative' }}>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowCandidateModal(false)}
                  style={{ position: 'absolute', top: '1rem', right: '1rem' }}
                ></button>
              </div>
              <div style={{ textAlign: 'center', marginTop: '-3rem', marginBottom: '0.5rem' }}>
                {selectedCandidate.photoUrl ? (
                  <img
                    src={selectedCandidate.photoUrl}
                    alt={selectedCandidate.user?.fullName}
                    style={{ width: '96px', height: '96px', objectFit: 'cover', borderRadius: '50%', border: '4px solid #fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}
                  />
                ) : (
                  <div style={{ width: '96px', height: '96px', borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '2.5rem', fontWeight: '700', border: '4px solid #fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                    {selectedCandidate.user?.fullName?.charAt(0) || '?'}
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="modal-body" style={{ padding: '0.5rem 2rem 2rem' }}>
                <div className="text-center mb-3">
                  <h4 className="mb-0" style={{ fontWeight: '700', color: '#1e293b', fontSize: '1.5rem' }}>
                    {selectedCandidate.user?.fullName || 'Unknown'}
                  </h4>
                </div>
                
                {selectedCandidate.bio && (
                  <div>
                    <h6 className="mb-2" style={{ fontWeight: '600', color: '#4b5563', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Biography</h6>
                    <p style={{ fontSize: '1rem', lineHeight: '1.7', color: '#6b7280', whiteSpace: 'pre-wrap' }}>
                      {selectedCandidate.bio}
                    </p>
                  </div>
                )}
                
                {showVoteCounts && selectedCandidate.voteCount !== null && selectedCandidate.voteCount !== undefined && (
                  <div className="d-flex align-items-center gap-2 mt-4 pt-3" style={{ borderTop: '1px solid #e5e7eb' }}>
                    <i className="bi bi-bar-chart-fill" style={{ color: '#667eea', fontSize: '1.25rem' }}></i>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>Current Votes:</span>
                    <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#2c3e50' }}>{selectedCandidate.voteCount}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {showCandidateModal && (
        <div
          className="modal-backdrop fade show"
          onClick={() => setShowCandidateModal(false)}
        ></div>
      )}

      {/* Payment Method Selection Modal */}
      {showPaymentModal && paymentOptions && (
        <>
          <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" style={{ borderRadius: '16px', border: 'none' }}>
                <div className="modal-header" style={{ borderBottom: '2px solid #f1f5f9', padding: '1.5rem' }}>
                  <h5 className="modal-title" style={{ fontWeight: '700', color: '#2c3e50' }}>
                    <i className="bi bi-wallet2 me-2" style={{ color: '#667eea' }}></i>
                    Choose Payment Method
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowPaymentModal(false);
                      setPendingVote(null);
                      setPaymentOptions(null);
                    }}
                  ></button>
                </div>
                <div className="modal-body" style={{ padding: '1.5rem' }}>
                  <div className="mb-3">
                    <div className="alert alert-info" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-info-circle me-2" style={{ color: '#3b82f6' }}></i>
                        <div style={{ fontSize: '0.9rem' }}>
                          <strong>Vote Price:</strong> {paymentOptions.votePrice.toLocaleString()} {paymentOptions.currency}<br />
                          <strong>Service Fee:</strong> {paymentOptions.serviceCharge.toLocaleString()} {paymentOptions.currency}<br />
                          <strong>Total:</strong> <span style={{ fontWeight: '700', color: '#667eea' }}>{paymentOptions.totalAmount.toLocaleString()} {paymentOptions.currency}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Wallet Option */}
                  <div
                    className="card mb-3"
                    onClick={() => paymentOptions.canUseWallet && handlePaymentMethodSelection('wallet')}
                    style={{
                      cursor: paymentOptions.canUseWallet ? 'pointer' : 'not-allowed',
                      border: '2px solid ' + (paymentOptions.canUseWallet ? '#10b981' : '#e5e7eb'),
                      borderRadius: '12px',
                      opacity: paymentOptions.canUseWallet ? 1 : 0.6,
                      transition: 'all 0.3s'
                    }}
                  >
                    <div className="card-body" style={{ padding: '1.25rem' }}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex align-items-start">
                          <div
                            className="me-3"
                            style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '12px',
                              background: paymentOptions.canUseWallet ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#e5e7eb',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '1.5rem'
                            }}
                          >
                            <i className="bi bi-wallet2"></i>
                          </div>
                          <div>
                            <h6 className="mb-1" style={{ fontWeight: '700', color: '#2c3e50' }}>
                              Use Wallet Balance
                            </h6>
                            <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                              Current Balance: <strong>{paymentOptions.walletBalance.toLocaleString()} {paymentOptions.walletCurrency || paymentOptions.currency}</strong>
                            </p>
                            {!paymentOptions.canUseWallet && (
                              <p className="mb-0 mt-1" style={{ fontSize: '0.813rem', color: '#ef4444' }}>
                                <i className="bi bi-exclamation-circle me-1"></i>
                                {paymentOptions.currencyMismatch 
                                  ? `Currency mismatch: Your wallet is in ${paymentOptions.walletCurrency} but this vote requires ${paymentOptions.currency}.` 
                                  : 'Insufficient balance. Please top up your wallet.'}
                              </p>
                            )}
                          </div>
                        </div>
                        {paymentOptions.canUseWallet && (
                          <i className="bi bi-check-circle-fill" style={{ color: '#10b981', fontSize: '1.25rem' }}></i>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Direct Payment Option */}
                  <div
                    className="card"
                    onClick={() => handlePaymentMethodSelection('direct')}
                    style={{
                      cursor: 'pointer',
                      border: '2px solid #667eea',
                      borderRadius: '12px',
                      transition: 'all 0.3s'
                    }}
                  >
                    <div className="card-body" style={{ padding: '1.25rem' }}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="d-flex align-items-start">
                          <div
                            className="me-3"
                            style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '1.5rem'
                            }}
                          >
                            <i className="bi bi-credit-card"></i>
                          </div>
                          <div>
                            <h6 className="mb-1" style={{ fontWeight: '700', color: '#2c3e50' }}>
                              Direct Payment
                            </h6>
                            <p className="mb-0 text-muted" style={{ fontSize: '0.875rem' }}>
                              Pay with Mobile Money, Card or Bank
                            </p>
                          </div>
                        </div>
                        <i className="bi bi-arrow-right-circle-fill" style={{ color: '#667eea', fontSize: '1.25rem' }}></i>
                      </div>
                    </div>
                  </div>

                  {!paymentOptions.canUseWallet && (
                    <div className="mt-3">
                      <Link
                        to="/wallet"
                        className="btn btn-outline-primary btn-sm w-100"
                        style={{ borderRadius: '8px' }}
                      >
                        <i className="bi bi-wallet2 me-2"></i>
                        Top Up Wallet
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => {
              setShowPaymentModal(false);
              setPendingVote(null);
              setPaymentOptions(null);
            }}
          ></div>
        </>
      )}
    </div>
  );
};

export default VotingPage;
